import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactElement } from "react";
import { createPortal } from "react-dom";
import { Spinner } from "@fluentui/react-components";
import { runHandler } from "../../../integrations/tools";
import { execCommandCopyFallback } from "../../../integrations/tools/clipboard";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import { useDocReviewFindings } from "../hooks/useDocReviewFindings";
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, CopyIcon } from "../../shared/components/icons";
import { findingsListText } from "../copyList";
import {
  firstUndecided,
  groupFindings,
  isDecided,
  isUnmarked,
  reviewTotals,
  type CategoryGroup,
  type ReviewTotals,
  type RuleGroup,
} from "../grouping";
import { findingLocation, isLocatable } from "../location";
import { fixSummary, isCommentFix, sortOrderNote, type RuleGroupId } from "../ruleCatalog";
import { getScanNotes, subscribeScanNotes } from "../scanNotes";
import type { Finding } from "../types";
import styles from "./DocReviewPanel.module.css";

interface DocReviewPanelProps {
  /** The conversation the `/check-references` skill ran in. Null before any
   *  scan has run this session — the panel then shows the empty state. */
  readonly threadId: string | null;
  readonly apiToken: string | null;
  readonly refreshTokens?: RefreshTokenFn;
  /** Where the "Copy as list" bar goes: a slot the chat keeps directly above
   *  its input box, outside the scrolling thread, so the bar stays put however
   *  far the list scrolls. Without one (or before it mounts) the bar sits
   *  under the groups instead. */
  readonly footerSlot?: HTMLElement | null;
}

/** Where a rule's pager opens: its first finding still to decide, or the
 *  first finding when every one is decided. */
function firstToDecide(rule: RuleGroup): number {
  return Math.max(0, rule.findings.findIndex((f) => !isDecided(f)));
}

function plural(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** "N to review · X of Y decided" — empty until the review has findings. */
function ReviewCount({ totals }: Readonly<{ totals: ReviewTotals }>) {
  if (totals.total === 0) return null;
  const undecided = totals.total - totals.decided;
  return (
    <>
      <strong className={styles.toReview}>{undecided > 0 ? `${undecided} to review` : "All decided"}</strong> ·{" "}
      {totals.decided} of {totals.total} decided
    </>
  );
}

/** The progress bar and its applied / skipped / couldn't-be-applied line. */
function ReviewProgress({ totals }: Readonly<{ totals: ReviewTotals }>) {
  if (totals.total === 0) return null;
  const percent = Math.round((totals.decided / totals.total) * 100);
  return (
    <>
      <progress className={styles.progress} aria-label="Review progress" max={100} value={percent} />
      <div className={styles.substat}>
        {totals.applied} applied · {totals.skipped} skipped
        {totals.stale > 0 && (
          <span className={styles.staleCount}> · {plural(totals.stale, "finding")} couldn't be applied</span>
        )}
        {totals.unmarked > 0 && (
          <span className={styles.staleCount}> · {plural(totals.unmarked, "finding")} not marked in the document</span>
        )}
      </div>
    </>
  );
}

/** What the panel shows instead of the review, or null once there are
 *  findings to review. */
function emptyState(threadId: string | null, loading: boolean, findingCount: number): ReactElement | null {
  if (!threadId) {
    return <p className={styles.empty}>Run /check-references in the chat to start a document review.</p>;
  }
  if (findingCount > 0) return null;
  if (loading) return <Spinner label="Loading findings…" />;
  // AIVA-8554: the panel mounts only after a check has run, so an empty list
  // means that check found nothing new — not that none has run. A re-run after
  // every finding was applied lands here, and "yet" read as a failed check.
  return <p className={styles.empty}>Nothing to review — this check found no new issues.</p>;
}

/** What the check could not cover (AIVA-9191) — shown with or without findings,
 *  so "no issues" is never read as "everything was checked". */
function ScanNotes({ notes }: Readonly<{ notes: readonly string[] }>) {
  if (notes.length === 0) return null;
  return (
    <div className={styles.scanNotes} role="note">
      {notes.map((note) => (
        <p key={note}>{note}</p>
      ))}
    </div>
  );
}

/** Above the groups: "nothing left" once every finding is decided. There is
 *  deliberately no bulk fix — the operator decides each finding on its own
 *  (AIVA-8875), where they can see what is being applied. */
function ReviewActions({ totals }: Readonly<{ totals: ReviewTotals }>) {
  if (totals.total !== totals.decided || totals.stale !== 0) return null;
  return (
    <p className={styles.allClear}>
      Nothing left to review — {totals.applied} applied, {totals.skipped} skipped. Use Done reviewing to remove the
      hidden markers.
    </p>
  );
}

/** The "Copy as list" row: the button, then the counts — or the copy error. */
function CopyBar({
  copied,
  failed,
  totals,
  onCopy,
}: Readonly<{ copied: boolean; failed: boolean; totals: ReviewTotals; onCopy: () => void }>) {
  return (
    <div className={styles.copyBar}>
      <button type="button" className={`${styles.copyBtn} ${copied ? styles.copied : ""}`} onClick={onCopy}>
        {copied ? <CheckIcon /> : <CopyIcon />}
        {copied ? "Copied" : "Copy as list"}
      </button>
      {failed ? (
        <span className={styles.copyError} role="alert">
          Couldn't copy — try again.
        </span>
      ) : (
        <span className={styles.copySummary}>
          {plural(totals.total, "issue")} · {totals.total - totals.decided} open
        </span>
      )}
    </div>
  );
}

/** Put `text` on the clipboard: the async API first, then the legacy
 *  execCommand path for Office hosts whose iframe Permissions-Policy blocks it
 *  — the same order every other Copy button in the add-in uses. */
async function copyText(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Blocked by the host — fall through to execCommand.
    }
  }
  return execCommandCopyFallback(text);
}

/** What was last copied: the whole review, or one group — and whether it worked. */
interface CopyFeedback {
  readonly scope: RuleGroupId | "all";
  readonly ok: boolean;
}

const COPY_FEEDBACK_MS = 2500;

/**
 * Collective review (AIVA-8751): every finding of the current
 * document-review thread, grouped (cross-references, formatting & links,
 * defined terms, numbering & order) and then by rule. A rule opens to a
 * one-line summary of its current finding with a pager. Every rule starts
 * closed, and a cleared rule closes rather than opening the next one: the
 * operator chooses which rule to work through (AIVA-8875).
 *
 * Every write goes through the hook's per-finding apply/skip, so the
 * staleness guard, anchor repair and verification each finding gets on its
 * own apply all hold.
 */
export function DocReviewPanel({ threadId, apiToken, refreshTokens, footerSlot }: Readonly<DocReviewPanelProps>) {
  const { findings, loading, error, actingId, clearFindings, applyFinding, skipFinding, jumpToFinding } =
    useDocReviewFindings(threadId, apiToken, refreshTokens);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [cleanupMessage, setCleanupMessage] = useState<string | null>(null);

  const groups = useMemo(() => groupFindings(findings), [findings]);
  const totals = useMemo(() => reviewTotals(findings), [findings]);

  const [openGroup, setOpenGroup] = useState<RuleGroupId | null>(null);
  const [openRule, setOpenRule] = useState<string | null>(null);
  const [pageByRule, setPageByRule] = useState<Record<string, number>>({});
  const busy = actingId !== null;

  // AIVA-8750: copy the findings as a list for the attorney.
  const [copyFeedback, setCopyFeedback] = useState<CopyFeedback | null>(null);
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    [],
  );

  const handleCopy = async (scope: RuleGroupId | "all") => {
    const ok = await copyText(findingsListText(findings, scope === "all" ? undefined : scope));
    if (copyTimer.current) clearTimeout(copyTimer.current);
    setCopyFeedback({ scope, ok });
    copyTimer.current = setTimeout(() => setCopyFeedback(null), COPY_FEEDBACK_MS);
  };

  const copiedHere = (scope: RuleGroupId | "all") => copyFeedback?.scope === scope && copyFeedback.ok;

  // Open the first group with work left as soon as a batch of findings
  // arrives — and again after "Done reviewing" empties the list and a later
  // scan fills it — but never re-open on an ordinary update, or every
  // apply would yank the operator back to the top. Its rules stay closed
  // (AIVA-8875): opening the first one showed one rule's findings and hid
  // the rest, so it read as the only rule with anything to review.
  const opened = useRef(false);
  useEffect(() => {
    if (findings.length === 0) {
      opened.current = false;
      return;
    }
    if (opened.current) return;
    opened.current = true;
    setOpenGroup(firstUndecided(groups)?.categoryId ?? groups[0]?.id ?? null);
    setOpenRule(null);
  }, [findings.length, groups]);

  // A decided finding stays on its card, marked done (AIVA-8875): applying
  // or skipping one used to drop it from the pager, so the card the operator
  // had just acted on vanished. The pager walks every finding of the rule, in
  // a fixed order, and moving on is the operator's own Next.
  const handleApply = async (finding: Finding) => {
    await applyFinding(finding);
  };

  const handleSkip = async (finding: Finding) => {
    await skipFinding(finding);
  };

  const toggleGroup = (groupId: RuleGroupId) => {
    setOpenGroup(openGroup === groupId ? null : groupId);
    setOpenRule(null);
  };

  /** Opening a rule lands on its first finding still to decide, and the page
   *  is pinned there, so a decision leaves the card in place instead of the
   *  pager jumping on to the next undecided one. */
  const toggleRule = (rule: RuleGroup) => {
    if (openRule === rule.ruleId) {
      setOpenRule(null);
      return;
    }
    setOpenRule(rule.ruleId);
    setPageByRule((pages) =>
      pages[rule.ruleId] === undefined ? { ...pages, [rule.ruleId]: firstToDecide(rule) } : pages,
    );
  };

  const turnPage = (rule: RuleGroup, delta: number) =>
    setPageByRule((pages) => {
      const from = pages[rule.ruleId] ?? firstToDecide(rule);
      const page = Math.min(Math.max(0, from + delta), Math.max(0, rule.findings.length - 1));
      return { ...pages, [rule.ruleId]: page };
    });

  const handleDoneReviewing = async () => {
    setCleaningUp(true);
    setCleanupMessage(null);
    try {
      const result = await runHandler("word", "word.docreview.cleanup_anchors", {}, 10000);
      const removed = (result.data as { removed?: number } | undefined)?.removed ?? 0;
      const markerWord = removed === 1 ? "marker" : "markers";
      setCleanupMessage(
        result.ok
          ? `Done — removed ${removed} hidden ${markerWord} from the document.`
          : `Could not clean up markers: ${result.error ?? "unknown error"}`,
      );
      // AIVA-8180: cleanup_anchors only removes the document's own hidden
      // content controls — it never touches the underlying Cosmos records,
      // so the findings this panel is still showing are now anchor-less.
      // Clear them locally rather than leaving cards the operator can no
      // longer act on (Apply/jump-to-location would just fail against a
      // missing anchor). Only on a successful cleanup: if it failed, the
      // anchors may still be there and the findings are still good.
      if (result.ok) {
        clearFindings();
        setPageByRule({});
      }
    } finally {
      setCleaningUp(false);
    }
  };

  const renderSummary = (finding: Finding) => {
    const staleLine = finding.status === "stale" && (
      <strong className={styles.staleLine}>Could not be applied — the text changed since the check ran.</strong>
    );
    // AIVA-9179: shown instead of nothing when marking never reached this one.
    const unmarkedLine = isUnmarked(finding) && (
      <span className={styles.unmarkedLine}>Not marked in the document — it will be found by its text when opened.</span>
    );
    // AIVA-8606: an out-of-order finding involving an acronym explains how it
    // was sorted ("NTL has no full form…, so it is sorted by abbreviation").
    // Shown as the old finding card showed it — the problem, then the whole
    // comment — since without the note the order can look like a bug.
    if (sortOrderNote(finding)) {
      return (
        <div className={`${styles.strip} ${finding.status === "stale" ? styles.stripStale : ""}`}>
          <strong className={styles.problemLine}>{finding.problem}</strong>
          <span className={styles.note}>{finding.comment}</span>
          {staleLine}
          {unmarkedLine}
        </div>
      );
    }
    const { matchText } = findingLocation(finding);
    const summary = fixSummary(finding);
    return (
      <div className={`${styles.strip} ${finding.status === "stale" ? styles.stripStale : ""}`}>
        {matchText && <span className={styles.from}>{matchText}</span>}
        {matchText && " → "}
        <span className={summary.kind === "replace" ? styles.to : styles.note}>{summary.text}</span>
        {staleLine}
        {unmarkedLine}
      </div>
    );
  };

  /** In place of a decided finding's buttons: what was done with it. */
  const renderDecision = (finding: Finding) => {
    if (finding.status === "skipped") return <output className={`${styles.decision} ${styles.decisionSkipped}`}>Skipped</output>;
    return (
      <output className={`${styles.decision} ${styles.decisionDone}`}>
        {isCommentFix(finding) ? "✓ Comment inserted" : "✓ Fixed"}
      </output>
    );
  };

  const renderRuleDetail = (rule: RuleGroup) => {
    if (rule.findings.length === 0) return null;

    const total = rule.findings.length;
    const page = Math.min(pageByRule[rule.ruleId] ?? firstToDecide(rule), total - 1);
    const current = rule.findings[page]!;
    const locatable = isLocatable(current);
    const applyDisabled = busy || current.status === "stale" || !locatable;
    return (
      <div className={styles.ruleDetail}>
        {renderSummary(current)}
        <div className={styles.pager}>
          <button
            type="button"
            className={styles.navBtn}
            onClick={() => turnPage(rule, -1)}
            disabled={page === 0}
            aria-label="Previous finding"
          >
            <ChevronLeftIcon />
          </button>
          <span className={styles.pageCount}>
            {page + 1} / {total}
          </span>
          <button
            type="button"
            className={styles.navBtn}
            onClick={() => turnPage(rule, 1)}
            disabled={page === total - 1}
            aria-label="Next finding"
          >
            <ChevronRightIcon />
          </button>
          <span className={styles.spacer} />
          <button
            type="button"
            className={styles.eye}
            onClick={() => void jumpToFinding(current)}
            disabled={!locatable}
            aria-label="Show in document"
            title={locatable ? "Show in document" : "No location recorded for this finding"}
          >
            <span aria-hidden="true">👁</span>
          </button>
        </div>
        {isDecided(current) ? (
          renderDecision(current)
        ) : (
          <div className={styles.actions}>
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.primary}`}
              onClick={() => void handleApply(current)}
              disabled={applyDisabled}
              title={locatable ? undefined : "This finding no longer has a location in the document — re-run the check"}
            >
              {isCommentFix(current) ? "Insert comment" : "Fix this one"}
            </button>
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.skip}`}
              onClick={() => void handleSkip(current)}
              disabled={busy}
            >
              Skip
            </button>
          </div>
        )}
        {rule.judged && !isDecided(current) && (
          <p className={styles.judgedNote}>Needs judgment — review each one before applying it.</p>
        )}
      </div>
    );
  };

  const renderRule = (rule: RuleGroup) => {
    const isOpenRule = openRule === rule.ruleId;
    const done = rule.undecided.length === 0;
    const appliedCount = rule.findings.filter((f) => f.status === "applied" || f.status === "verified").length;
    let tone = styles.toneOpen;
    if (done) tone = styles.toneDone;
    else if (rule.judged) tone = styles.toneJudged;

    return (
      <div key={rule.ruleId} className={`${styles.rule} ${done ? styles.ruleDone : ""}`}>
        <div className={styles.ruleRow}>
          {/* A finished rule still opens: its cards stay, each marked with
              what was done (AIVA-8875). */}
          <button
            type="button"
            className={styles.ruleToggle}
            onClick={() => toggleRule(rule)}
            aria-expanded={isOpenRule}
          >
            <span className={`${styles.dot} ${tone}`} aria-hidden="true" />
            <span className={styles.ruleName}>{rule.name}</span>
          </button>
          <span className={`${styles.affected} ${tone}`}>
            {done ? `fixed ${appliedCount}` : `${rule.undecided.length} affected`}
          </span>
          <button
            type="button"
            className={styles.caret}
            onClick={() => toggleRule(rule)}
            aria-hidden="true"
            tabIndex={-1}
          >
            {isOpenRule ? "▲" : "▼"}
          </button>
        </div>
        {isOpenRule && renderRuleDetail(rule)}
      </div>
    );
  };

  const renderGroup = (group: CategoryGroup) => {
    const isOpenGroup = openGroup === group.id;
    const done = group.undecidedCount === 0;
    return (
      <section
        key={group.id}
        className={`${styles.group} ${isOpenGroup ? styles.groupOpen : ""} ${done ? styles.groupDone : ""}`}
      >
        <div className={styles.groupHead}>
          <button
            type="button"
            className={styles.groupToggle}
            onClick={() => toggleGroup(group.id)}
            aria-expanded={isOpenGroup}
          >
            <span className={styles.twisty} aria-hidden="true">
              {isOpenGroup ? "▼" : "►"}
            </span>
            <span className={styles.groupName}>
              {done ? "✓ " : ""}
              {group.label}
            </span>
            <span className={styles.groupCount}>{done ? "done" : plural(group.undecidedCount, "issue")}</span>
          </button>
          <button
            type="button"
            className={`${styles.groupCopy} ${copiedHere(group.id) ? styles.copied : ""}`}
            onClick={() => void handleCopy(group.id)}
            // Named for the action, not the group: the group's own toggle is
            // the button named after it.
            aria-label={copiedHere(group.id) ? "Group copied" : "Copy this group as a list"}
            title={copiedHere(group.id) ? "Copied" : `Copy ${group.label} as a list`}
          >
            {copiedHere(group.id) ? <CheckIcon /> : <CopyIcon />}
          </button>
        </div>
        {isOpenGroup && <div className={styles.groupBody}>{group.rules.map(renderRule)}</div>}
      </section>
    );
  };

  const empty = emptyState(threadId, loading, findings.length);
  const scanNotes = useSyncExternalStore(subscribeScanNotes, () => getScanNotes(threadId));
  const copyBar = empty ? null : (
    <CopyBar
      copied={copiedHere("all")}
      failed={copyFeedback?.ok === false}
      totals={totals}
      onCopy={() => void handleCopy("all")}
    />
  );
  const content = empty ?? (
    <>
      <ReviewActions totals={totals} />
      {groups.map(renderGroup)}
      {footerSlot ? null : copyBar}
    </>
  );

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.count}>
          <ReviewCount totals={totals} />
        </span>
        <button
          type="button"
          className={styles.doneLink}
          onClick={() => void handleDoneReviewing()}
          disabled={cleaningUp}
        >
          Done reviewing
        </button>
      </div>

      <ReviewProgress totals={totals} />

      {error && (
        <div className={styles.error} role="alert">
          {error}
        </div>
      )}
      {cleanupMessage && <output className={styles.count}>{cleanupMessage}</output>}

      <ScanNotes notes={scanNotes} />
      {content}
      {footerSlot && copyBar ? createPortal(copyBar, footerSlot) : null}
    </div>
  );
}
